import { Api } from '@/Api';
import { defineStore } from 'pinia';

export const PRO_STRING_PREFIX = 'pro';
export const DISCOUNT_MAX_SEATS = 3;

export enum TeamPlan {
  TEAM_MONTHLY = 'team-monthly',
  TEAM_ANNUAL = 'team-annual',
  BUSINESS_MONTHLY = 'business-monthly',
  BUSINESS_ANNUAL = 'business-annual',
}
export interface OrganizationState {
  id?: string;
  name?: string;
  links: {
    invitationUrl?: string;
    invoicesUrl?: string;
  };
  free?: boolean;
  tier?: string;
  membersCount: number;
  seatsCount: number;
  seatsLeft: number;
  trial?: boolean;
  isPaying?: boolean;
  planCode?: string;
  versioningEnabled?: boolean;
  customStorage?: boolean;
  ownerId?: string;
  logo?: string;
  hideCloudappBranding?: boolean;
  redirectRootUrl?: string;
  preferences: {
    itemsRetention?: number;
    itemsRemoveAction?: string;
    itemsRemoveEnabledAt?: string;
    webhookUrl?: string;
  };
  requestContentSettings: {
    logCollectionDomain?: string;
    logCollectionDomains?: string[];
    defaultTitle?: string;
    defaultDescription?: string;
    endDescription?: string;
  };
}

export const useOrganizationStore = defineStore('organizationStore', {
  state: () =>
    ({
      id: undefined,
      name: undefined,
      free: undefined,
      tier: undefined,
      membersCount: 0,
      seatsCount: 0,
      seatsLeft: 0,
      trial: undefined,
      isPaying: undefined,
      planCode: undefined,
      versioningEnabled: undefined,
      customStorage: undefined,
      ownerId: undefined,
      logo: undefined,
      hideCloudappBranding: undefined,
      redirectRootUrl: undefined,
      links: {
        invitationUrl: undefined,
        invoicesUrl: undefined,
      },
      preferences: {
        itemsRetention: undefined,
        itemsRemoveAction: undefined,
        itemsRemoveEnabledAt: undefined,
        webhookUrl: undefined,
      },
      requestContentSettings: {
        logCollectionDomain: undefined,
        logCollectionDomains: [],
        defaultTitle: undefined,
        defaultDescription: undefined,
        endDescription: undefined,
      },
    }) as OrganizationState,
  actions: {
    initialize(orgData: any) {
      const membersCount = orgData.data.relationships.members.data.length;

      this.$patch({
        id: orgData.data.id,
        links: {
          ...orgData.data.links,
        },
        ...orgData.data.attributes,
        membersCount,
        requestContentSettings: {
          // Backend sends in camelCase (logCollectionDomain) not snake_case (log_collection_domain)
          logCollectionDomain: orgData.data.attributes.logCollectionDomain || orgData.data.attributes.log_collection_domain,
          logCollectionDomains: orgData.data.attributes.logCollectionDomains || orgData.data.attributes.log_collection_domains || [],
          defaultTitle: orgData.data.attributes.requestVideoDefaultTitle || orgData.data.attributes.request_video_default_title,
          defaultDescription: orgData.data.attributes.requestVideoDefaultDescription || orgData.data.attributes.request_video_default_description,
          endDescription: orgData.data.attributes.requestVideoEndDescription || orgData.data.attributes.request_video_end_description,
        },
      });

      if (orgData.data?.relationships?.owner) {
        this.ownerId = orgData.data.relationships.owner.data.id;
      }

      return orgData;
    },
    sendOrgInvitations(emails: string[], source: string = 'invite-collaborators') {
      if (!this.id) {
        return Promise.reject('No organization id set');
      }

      return Api.organization.inviteMembers(this.id, emails, source);
    },
    async updateWebhookUrl(webhookUrl: string) {
      if (!this.id) {
        throw new Error('No organization id set');
      }

      await Api.organization.update(this.id, {
        webhook_url: webhookUrl,
      });

      this.$patch({
        preferences: {
          ...this.preferences,
          webhookUrl: webhookUrl,
        },
      });
    },
  },
  getters: {
    showMemberDiscount: (state: OrganizationState) => state.planCode?.includes(PRO_STRING_PREFIX) && state.membersCount < DISCOUNT_MAX_SEATS,
    invitationUrl: (state: OrganizationState) => state.links.invitationUrl,
    itemsRemoveEnabledAt: (state: OrganizationState) => state.preferences.itemsRemoveEnabledAt,
    itemsRetentionHumanReadable: (state: OrganizationState) => {
      if (!state.preferences.itemsRetention) {
        return '';
      }

      const { itemsRetention } = state.preferences;
      // intervals used in settings
      const intervals = [
        { seconds: 60 * 60, label: '1 hour' },
        { seconds: 60 * 60 * 24, label: '1 day' },
        { seconds: 60 * 60 * 24 * 7, label: '1 week' },
        { seconds: 60 * 60 * 24 * 30, label: '1 month' },
        { seconds: 60 * 60 * 24 * 90, label: '3 months' },
        { seconds: 60 * 60 * 24 * 180, label: '6 months' },
      ];

      const interval = intervals.find(i => i.seconds === itemsRetention);
      return interval ? interval.label : `${itemsRetention} seconds`;
    },
    /**
     * Determines if the organization can access workflow integrations (Paragon).
     * See TeamPlan enum for the list of eligible plans.
     */
    canAccessWorkflowIntegrations: (state: OrganizationState) => {
      const planCode = state.planCode?.toLowerCase();
      return planCode && Object.values(TeamPlan).includes(planCode as TeamPlan);
    },
    teamPlans: () => Object.values(TeamPlan),
    itemsRemoveAction: (state: OrganizationState) => state.preferences.itemsRemoveAction,
    requestContentLogEnabled: (state: OrganizationState) => Boolean(state.requestContentSettings?.logCollectionDomain),
    requestContentLogDomain: (state: OrganizationState) => state.requestContentSettings?.logCollectionDomain || 'example.com',
  },
});
